import React from "react";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import CssBaseline from "@mui/material/CssBaseline";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { connect } from "react-redux";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import Typography from "@mui/material/Typography";
import ScrollToTopBtn from "../common/ScrollToTop";
import * as ClearingUtils from "./ClearingUtils";
import { withParams } from "../common/WithParams";

class ClearingDetailsUser extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      clearing: {},
      clearings: [],
      type: "Compensations",
      membership: props.type,
      page: 0,
      rowsPerPage: 25,
      sortDirection: "desc",
    };
  }

  async componentDidMount() {
    try {
      const { id } = this.props.params;
      const membership = this.props.type;
      let clearing = null;

      if (id) {
        clearing = await ClearingUtils.getClearing(id);
      } else {
        clearing = await this.fetchLatestClearing(membership || "TFF");
      }
      if (clearing) {
        this.setState({ clearing });
        await this.fetchClearings(clearing.clearingId);
      }
    } catch (error) {
      console.log("Error in componentDidMount", error);
    }
  }

  async componentDidUpdate(prevProps) {
    if (
      this.props.type !== prevProps.type ||
      this.props.params.id !== prevProps.params.id
    ) {
      this.setState({ membership: this.props.type });

      try {
        let clearing = null;
        if (this.props.params.id) {
          clearing = await ClearingUtils.getClearing(this.props.params.id);
        } else {
          clearing = await this.fetchLatestClearing(this.props.type || "TFF");
        }

        if (clearing) {
          this.setState({ clearing });
          await this.fetchClearings(clearing.clearingId);
        }
      } catch (error) {
        console.log("Error in componentDidUpdate", error);
      }
    }
  }

  fetchLatestClearing = async (membership) => {
    try {
      const clearingList = await ClearingUtils.getClearings(membership, "");
      if (clearingList && clearingList.length > 0) {
        clearingList.sort(this.compareBy("toDate", "desc"));
        return clearingList[0];
      }
      return null;
    } catch (error) {
      console.error("Error fetching the latest clearing:", error);
      return null;
    }
  };

  fetchClearings = async (clearingId) => {
    try {
      const { type } = this.state;
      const companyId = this.props.signedinuser.companyId;
      const clearings = await ClearingUtils.getClearingsById(type, clearingId, companyId);
      this.setState({ clearings });
    } catch (error) {
      console.error("Error fetching clearings:", error);
      this.setState({ clearings: [] });
    }
  };

  onChange = async (e) => {
    const newType = e.target.value;
    this.setState({ type: newType }, async () => {
      await this.fetchClearings(this.state.clearing.clearingId);
    });
  };

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: +event.target.value, page: 0 });
  };

  subtotal = (column) => {
    const { clearings } = this.state;
    return clearings
      .reduce((total, clearing) => total + (clearing[column.id] || 0), 0)
      .toLocaleString("de-DE");
  };

  handledownload = async () => {
    const { clearing, type } = this.state;
    const companyId = this.props.signedinuser.companyId;
    await ClearingUtils.getExcelFile(type, clearing.clearingId, companyId, true);
  };

  compareBy = (key, direction) => (a, b) => {
    if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
    if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
    return 0;
  };

  sortBy = (key) => {
    const { sortDirection, clearings } = this.state;
    const newDirection = sortDirection === "asc" ? "desc" : "asc";
    this.setState({
      clearings: [...clearings].sort(this.compareBy(key, newDirection)),
      sortDirection: newDirection,
    });
  };

  render() {
    const { clearing, type, page, rowsPerPage } = this.state;
    let columns = [];
    if (this.state.type === "Compensations") {
      columns = [
        { id: "damageYear", label: "Skadeår" },
        {
          id: "ib",
          label: "IB",
          align: "right",
          format: (value) => value.toLocaleString("de-DE"),
          title:
            "IB: Inngående balanse dvs inngående IBNR + RBNS fra måned 12 foregående år",
        },
        {
          id: "paid",
          label: "Betalt",
          align: "right",
          format: (value) => value.toLocaleString("de-DE"),
          title: "Betalt: utbetalt + innbetalt",
        },
        {
          id: "ub",
          label: "UB",
          align: "right",
          format: (value) => value.toLocaleString("de-DE"),
          title:
            "UB: Utgående balanse dvs utgående IBNR + RBNS for avregningsmåned",
        },
        {
          id: "clearing",
          label: "Påløpne erstatninger",
          align: "right",
          format: (value) => value.toLocaleString("de-DE"),
          title:
            "Påløpne erstatninger: For inneværende år - (Betalt + UB), ellers avviklingsresultat: IB - (Betalt + UB)",
        },
        {
          id: "percentage",
          label: "Andel(%)",
          align: "right",
          format: (value) =>
            value.toLocaleString(undefined, {
              minimumFractionDigits: 4,
              maximumFractionDigits: 4,
            }),
        },
        {
          id: "compIB",
          label: "IB",
          align: "right",
          format: (value) => value.toLocaleString("de-DE"),
        },
        {
          id: "compPaid",
          label: "Betalt",
          align: "right",
          format: (value) => value.toLocaleString("de-DE"),
        },
        {
          id: "compUB",
          label: "UB",
          align: "right",
          format: (value) => value.toLocaleString("de-DE"),
        },
        {
          id: "compClearing",
          label: "Påløpne erstatninger",
          align: "right",
          format: (value) => value.toLocaleString("de-DE"),
        },
      ];
    }
    if (this.state.type === "Costs") {
      columns = [
        { id: "damageYear", label: "Skadeår" },
        {
          id: "costs",
          label: "Omkostninger",
          align: "right",
          format: (value) => value.toLocaleString("de-DE"),
        },
        {
          id: "operatingExpenses",
          label: "Driftskostnader",
          align: "right",
          format: (value) => value.toLocaleString("de-DE"),
        },
        {
          id: "percentage",
          label: "Andel(%)",
          align: "right",
          format: (value) => value.toLocaleString("de-DE"),
        },
        {
          id: "compCosts",
          label: "Omkostninger",
          align: "right",
          format: (value) =>
            value.toLocaleString(undefined, {
              minimumFractionDigits: 4,
              maximumFractionDigits: 4,
            }),
        },
        {
          id: "compOperatingExpenses",
          label: "Driftskostnader",
          align: "right",
          format: (value) => value.toLocaleString("de-DE"),
        },
      ];
    }
    return (
      <React.Fragment>
        <Container maxWidth="lg" sx={{ paddingTop: "80px" }}>
          <main sx={{ width: "100%" }} >
            <Paper sx={{ width: "100%", p: 3 }}>
              <CssBaseline />

              {/* AVREGNING INFO */}
              <Grid container spacing={3}>
                <Grid item lg={3}>
                  <FormControl fullWidth>
                    <TextField
                      name="membership"
                      label="Organisasjon"
                      value={this.state.membership || ""}
                      fullWidth
                      variant="standard"
                      InputProps={{ readOnly: true }}
                    />
                  </FormControl>
                </Grid>
                <Grid item lg={3}>
                  <FormControl fullWidth>
                    <TextField
                      name="year"
                      label="År"
                      value={clearing.toDate ? new Date(clearing.toDate).getFullYear() : ""}
                      fullWidth
                      variant="standard"
                      InputProps={{ readOnly: true }}
                    />
                  </FormControl>
                </Grid>
                <Grid item lg={3}>
                  <FormControl fullWidth>
                    <TextField
                      name="month"
                      label="Måned"
                      value={clearing.toDate ? new Date(clearing.toDate).getMonth() + 1 : ""}
                      fullWidth
                      variant="standard"
                      InputProps={{ readOnly: true }}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              {/* FILTERING OPTIONS */}
              <Grid container alignItems="center" spacing={1} sx={{ mt: 3, ml: 2 }}>
                <Grid item sx={{ display: "flex", alignItems: "center" }}>
                  <Tooltip
                    title={
                      <span style={{ fontSize: 15 }}>
                        Vis alle beløp i hele kroner akkumulert til avregningsmåned
                        innenfor året fram til og med avregningsmåned
                      </span>
                    }
                    placement="bottom-start"
                  >
                    <Typography variant="h6" sx={{ pt: 2, mr: 2 }}>
                      Vis:
                    </Typography>
                  </Tooltip>
                </Grid>

                <Grid item>
                  <RadioGroup
                    row
                    name="type"
                    value={type}
                    onChange={this.onChange}
                    sx={{ mt: 2.2 }}
                  >
                    <Tooltip
                      title={
                        <span style={{ fontSize: 15 }}>
                          Erstatninger: Inn-/utgående avsetinger og netto betalinger pr skadeår
                        </span>
                      }
                      placement="bottom"
                    >
                      <FormControlLabel
                        value="Compensations"
                        control={<Radio />}
                        label="Erstatninger"
                      />
                    </Tooltip>
                    <Tooltip
                      title={
                        <span style={{ fontSize: 15 }}>
                          Kostnader: Saksomkostninger og TFF/YFFs interne kostnader pr skadeår
                        </span>
                      }
                      placement="bottom"
                    >
                      <FormControlLabel
                        value="Costs"
                        control={<Radio />}
                        label="Kostnader"
                      />
                    </Tooltip>
                  </RadioGroup>
                </Grid>


                <Grid item>
                  <Typography variant="h6" sx={{ pt: 2, mx: 2, ml: -0.5 }}>
                    for
                  </Typography>
                </Grid>

                <Grid item lg={3}>
                  <FormControl fullWidth>
                    <TextField
                      name="company"
                      label="Medlem"
                      value={this.props.signedinuser.companyName || ""}
                      fullWidth
                      variant="standard"
                      InputProps={{ readOnly: true }}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              {/* TABLE */}
              <Grid sx={{ mt: 4 }}>
                <Paper sx={{ overflow: "auto" }}>
                  <TableContainer>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          {columns &&
                            columns.map((column) => (
                              <Tooltip
                                key={column.id}
                                title={<span style={{ fontSize: 15 }}>{column.title || ""}</span>}
                                placement="bottom-start"
                              >
                                <TableCell
                                  sx={{
                                    backgroundColor: "#eeeeee",
                                    fontWeight: "bold",
                                    fontSize: 16,
                                  }}
                                  align={column.align}
                                  onClick={() => this.sortBy(column.id)}
                                >
                                  {column.label}
                                </TableCell>
                              </Tooltip>
                            ))}
                        </TableRow>
                      </TableHead>

                      {this.state.clearing && this.state.clearings.length > 0 ? (
                        <TableBody>
                          {this.state.clearings
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((clearing, index) => (
                              <TableRow hover tabIndex={-1} key={index}>
                                {columns.map((column) => {
                                  const value = clearing[column.id];
                                  return (
                                    <TableCell key={column.id} align={column.align}>
                                      {column.format && typeof value === "number"
                                        ? column.format(value)
                                        : value}
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            ))}
                          {/* SUBTOTAL ROW */}
                          <TableRow hover tabIndex={-1}>
                            {columns.map((column) => {
                              let sum = this.subtotal(column);
                              if (column.id === "damageYear") sum = "Total";
                              if (column.id === "percentage") sum = "";
                              return (
                                <TableCell key={column.id} align={column.align}>
                                  {sum}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        </TableBody>
                      ) : (
                        <TableBody>
                          <TableRow>
                            <TableCell colSpan={columns.length} align="center">
                              <Typography sx={{ textAlign: "center", pt: 4 }}>Ingen treff</Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      )}
                    </Table>
                  </TableContainer>

                  {/* PAGINATION */}
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={this.state.clearings.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={this.handleChangePage}
                    onRowsPerPageChange={this.handleChangeRowsPerPage}
                  />
                </Paper>
                {/* EXPORT BUTTON */}
                <Grid sx={{ mt: 3, pb: 3 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<CloudDownloadIcon />}
                    onClick={this.handledownload}
                  >
                    Vis i Excel
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </main>
        </Container>
        <ScrollToTopBtn />
      </React.Fragment>
    );
  }
}

function mapStateToProps(state) {
  const signedinuser = state.signedinuserobject
    ? state.signedinuserobject.signedInUser
    : sessionStorage.getItem("token")
      ? JSON.parse(sessionStorage.getItem("token"))
      : [];

  return { signedinuser: signedinuser };
}

export default withParams(connect(mapStateToProps)(ClearingDetailsUser));
